import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { Layout } from '../components/Layout';
import { apiFetch } from '../lib/api';
import type { TreeWithRole } from '@nasabtree/shared-types';

export function TreeListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [trees, setTrees] = useState<TreeWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    apiFetch<TreeWithRole[]>('/api/trees').then((res) => {
      if (res.ok) setTrees(res.data);
      setLoading(false);
    });
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    const res = await apiFetch<TreeWithRole>('/api/trees', {
      method: 'POST',
      body: JSON.stringify({ name_ar: nameAr, name_en: nameEn || null }),
    });
    if (res.ok) {
      setTrees((prev) => [res.data, ...prev]);
      setOpen(false);
      setNameAr('');
      setNameEn('');
    }
    setSaving(false);
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportError('');
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await apiFetch<TreeWithRole>('/api/trees/import', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setImportOpen(false);
        navigate(`/trees/${res.data.id}`);
      } else {
        setImportError(res.error);
      }
    } catch {
      setImportError(t('common.error'));
    }
    setImporting(false);
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('trees.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('trees.subtitle', 'إدارة أشجار العائلة')}</p>
        </div>

        <div className="flex items-center gap-2">
        {/* Import button */}
        <Dialog.Root open={importOpen} onOpenChange={setImportOpen}>
          <Dialog.Trigger asChild>
            <button className="border border-gray-300 text-gray-700 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1.5">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M9.25 13.25a.75.75 0 0 0 1.5 0V4.636l2.955 3.129a.75.75 0 0 0 1.09-1.03l-4.25-4.5a.75.75 0 0 0-1.09 0l-4.25 4.5a.75.75 0 1 0 1.09 1.03L9.25 4.636v8.614Z" />
                <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
              </svg>
              {t('trees.import')}
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl p-6 w-full max-w-md z-50">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-2">
                {t('trees.importTitle')}
              </Dialog.Title>
              <p className="text-sm text-gray-500 mb-4">{t('trees.importHint')}</p>
              {importError && (
                <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">{importError}</div>
              )}
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl py-8 px-4 cursor-pointer hover:border-brand-400 transition-colors">
                {importing ? (
                  <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 16V4m0 0-4 4m4-4 4 4M4 20h16" />
                    </svg>
                    <span className="text-sm text-gray-600">{t('trees.importSelectFile')}</span>
                  </>
                )}
                <input type="file" accept=".json" className="hidden" onChange={(e) => void handleImport(e)} disabled={importing} />
              </label>
              <div className="flex justify-end mt-4">
                <Dialog.Close asChild>
                  <button className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors">
                    {t('common.cancel')}
                  </button>
                </Dialog.Close>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger asChild>
            <button className="bg-brand-600 text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-brand-700 transition-colors shadow-sm flex items-center gap-1.5">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
              </svg>
              {t('trees.create')}
            </button>
          </Dialog.Trigger>

          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl p-6 w-full max-w-md z-50">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">
                {t('trees.create')}
              </Dialog.Title>

              <form onSubmit={(e) => void handleCreate(e)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('trees.name_ar')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    required
                    dir="rtl"
                    value={nameAr}
                    onChange={(e) => setNameAr(e.target.value)}
                    placeholder="مثال: آل محمد"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('trees.name_en')}
                  </label>
                  <input
                    dir="ltr"
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    placeholder="e.g. Al-Muhammad Family"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                  />
                </div>

                <div className="flex gap-3 justify-end pt-2">
                  <Dialog.Close asChild>
                    <button type="button" className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors">
                      {t('common.cancel')}
                    </button>
                  </Dialog.Close>
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-brand-600 text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {saving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : trees.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v18M12 3c-2 4-6 6-9 7m9-7c2 4 6 6 9 7" />
            </svg>
          </div>
          <p className="text-gray-900 font-semibold mb-1">{t('trees.emptyTitle', 'لا توجد أشجار بعد')}</p>
          <p className="text-sm text-gray-500">{t('trees.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trees.map((tree) => (
            <button
              key={tree.id}
              onClick={() => navigate(`/trees/${tree.id}`)}
              className="bg-white rounded-xl border border-gray-200 p-5 text-start hover:border-brand-400 hover:shadow-md transition-all group"
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-brand-100 transition-colors">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v18M12 3c-2 4-6 6-9 7m9-7c2 4 6 6 9 7" />
                  </svg>
                </div>
                <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                  {tree.role}
                </span>
              </div>
              <p className="font-bold text-gray-900 text-lg mt-3" dir="rtl">{tree.name_ar}</p>
              {tree.name_en && (
                <p className="text-sm text-gray-500 mt-0.5" dir="ltr">{tree.name_en}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
